import React, { useState, useRef, useMemo, useEffect } from "react";
import { Box, Chip, Popper, Paper, ClickAwayListener, TextField, Checkbox, Button, Typography, Divider, Tooltip } from "@mui/material";
import { Search as SearchIcon, MoreHoriz as MoreHorizIcon } from "@mui/icons-material";
import TagIcon from "../../assets/action-items/tag_icon.svg";
import EnterIcon from "../../assets/action-items/enter_icon.svg";
import ActionItemService from "../../services/actionItems/ActionItemService";
import { useQueryClient } from "@tanstack/react-query";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import dropdownArrowIcon from "../../assets/action-items/arrow-dropdown-icon.svg";

type Tag = { tagId: string; tagName: string };

type Props = {
  value: Tag[];
  canEdit?: boolean;
  tagsList: Tag[];
  onChange: (updatedTags: string[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
  noBorder?: boolean;
  row: any;
  rowAction?: any;
  hideDefaultIcon?: boolean;
  inlineSearch?: boolean;
  isNewTask?: boolean;
  defaultIcon?: string;
  inlineIconMode?: boolean;
  tooltipTitle?: string;
  forceOpen?: boolean;
  onForceChange?: (data: { tagIds: string[]; tagNames: string[] }) => void;
  saveButtonLabel?: string;
};

const ActionItemsTagsCell: React.FC<Props> = (
  { value, canEdit, tagsList, onChange, onOpen, onClose, 
    noBorder, row, rowAction, hideDefaultIcon, inlineSearch, isNewTask, defaultIcon, inlineIconMode, tooltipTitle, forceOpen, onForceChange,
    saveButtonLabel= "Save"
  }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(value?.map((v) => v.tagId));
  const [search, setSearch] = useState("");
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [hoveredTag, setHoveredTag] = useState<string | null>(null);
  const { showSnackbar } = useDisplaySnackbar();
  const [editAnchor, setEditAnchor] = useState<{
    anchorEl: Element | null;
    tag: string;
    originalName?: string;
  } | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const handleMouseEnter = (tagId: string) => {
    if (!editAnchor) setHoveredTag(tagId);
  };

  const handleMouseLeave = (_tagId: string) => {
    if (!editAnchor) setHoveredTag(null);
  };
  const [createdTag, setCreatedTag] = useState<Tag[]>([]);

  const [sortedTags, setSortedTags] = useState<Tag[]>([]);

  useEffect(() => {
    // when popper first opens → split once
    if ((open && sortedTags.length === 0) || !open) {
      const validTags = Array.isArray(tagsList) ? tagsList : [];
      const selectedTags = validTags.filter(tag =>
        value?.some(v => v.tagId === tag.tagId)
      );
      const unselectedTags = validTags.filter(
        tag => !value?.some(v => v.tagId === tag.tagId)
      );
  
      setSortedTags([...selectedTags, ...unselectedTags]);
    }
  }, [open, tagsList, value, sortedTags.length]);

  const defaultSelected = useMemo(() => value.map((v) => v.tagId),[value]);
  const mergedTags = useMemo(() => {
    if (!tagsList) return [];

    const backendTags = tagsList.filter((t:any) => !t.isDeleted);

    // Merge backend + created
    const all = [
      ...backendTags,
      ...createdTag.filter(ct => !backendTags.some(t => t.tagId === ct.tagId))
    ];

    return all.sort((a, b) => {
      const isA_Default = defaultSelected.includes(a.tagId);
      const isB_Default = defaultSelected.includes(b.tagId);

      const isA_Created = createdTag.some(ct => ct.tagId === a.tagId);
      const isB_Created = createdTag.some(ct => ct.tagId === b.tagId);

      const isA_Selected = selected.includes(a.tagId);
      const isB_Selected = selected.includes(b.tagId);

      // 1 - Default selected
      if (isA_Default !== isB_Default)
        return isA_Default ? -1 : 1;

      // 2 - Created + selected
      if ((isA_Created && isA_Selected) !== (isB_Created && isB_Selected))
        return (isA_Created && isA_Selected) ? -1 : 1;

      // 3 - Selected (but not default or created)
      const isA_SelectedOnly = isA_Selected && !isA_Default && !isA_Created;
      const isB_SelectedOnly = isB_Selected && !isB_Default && !isB_Created;

      if (isA_SelectedOnly !== isB_SelectedOnly)
        return isA_SelectedOnly ? -1 : 1;

      // 4 - Everything else (unsel)
      return 0;
    });

  }, [tagsList, createdTag, selected, defaultSelected]);

  const filteredTags = useMemo(() => {
    const lower = search.toLowerCase().trim();
    if (lower) {
      return mergedTags.filter(t =>
        t.tagName.toLowerCase().includes(lower)
      );
    }
    return mergedTags;
  }, [search, mergedTags]);
  const queryClient = useQueryClient();

  const { mutate: createTag } = ActionItemService.useCreateActionItemTags({
    onSuccess: (res: any) => {
      const newTag = {
        tagId: res.data.tagId,
        tagName: res.data.name,
      };
      setSelected((prev) => [...new Set([...prev, newTag.tagId])]);
      setCreatedTag(prev => [...prev, newTag]);
      setSortedTags(prev => {
        const existingSelected = prev.filter(
          t => selected.includes(t.tagId)
        );
        const existingUnselected = prev.filter(
          t => !selected.includes(t.tagId)
        );

        return [
          ...existingSelected,
          newTag,
          ...existingUnselected,
        ];
      });
      setSearch("");
      queryClient.invalidateQueries({ queryKey: ["get_action_item_tags"] });
    },
    onError: () => {
      showSnackbar( "error", "Failed to create tag.");
    },
  });
  const { mutate: updateTag } = ActionItemService.useUpdateActionItemTags({
    onSuccess: (_res: any) => {
      queryClient.invalidateQueries({ queryKey: ["get_action_item_tags"] });
      queryClient.invalidateQueries({ queryKey: ["get_my_action_items"]});
      queryClient.invalidateQueries({ queryKey: ["get_action_item_count"] });
      setEditAnchor(null);
    },
    onError: () => {
      showSnackbar("error", "Failed to update tag.");
    },
  });

  const { mutate: deleteTag } = ActionItemService.useDeleteActionItemTags({
    onSuccess: (_res: any, variables: any) => {
      if (variables?.tagId) {
        setSelected((prev) => prev.filter((id) => id !== variables.tagId));
        setSortedTags((prev) => prev.filter((tag) => tag.tagId !== variables.tagId));
      }
      setEditAnchor(null);
      queryClient.invalidateQueries({ queryKey: ["get_action_item_tags"] });
      queryClient.invalidateQueries({ queryKey: ["get_my_action_items"] });
      queryClient.invalidateQueries({ queryKey: ["get_action_item_count"] });
    },
    onError: () => {
      showSnackbar("error", "Failed to delete tag.");
    },
  });
  useEffect(() => {
    if (!open) {
      setSelected(value.map((v) => v.tagId));
    }
  }, [value, open]);

  useEffect(() => {
    setSelected((prevSelected) =>
      prevSelected.filter((id) =>
        tagsList?.some((tag) => tag.tagId === id)
      )
    );
  }, [tagsList]);

  const handleToggle = () => {
    if (!canEdit && !isNewTask) return;
    const newOpen = !open;
    setOpen(newOpen);
    if (newOpen) onOpen?.();
    else onClose?.();
  };

  const handleClose = () => {
    setOpen(false);
    setSearch("");
    setCreatedTag([]);
    setEditAnchor(null);
    setHoveredTag(null);
    onClose?.();
  };

  const handleSelect = (tag: Tag) => {
    setSelected((prev) => {
      const alreadySelected = prev.includes(tag.tagId);
      return alreadySelected ? prev.filter((id) => id !== tag.tagId) : [...prev, tag.tagId];
    });
  };

  const handleSave = () => {
  // Keep only created tags that the user has selected
    const filteredCreated = createdTag.filter(tag =>
      selected.includes(tag.tagId)
    );

    const updatedSelected = [
      ...new Set([
        ...selected.filter(id =>
          tagsList.some(tag => tag.tagId === id) ||
          createdTag.some(tag => tag.tagId === id)
        ),
        ...filteredCreated.map((tag: any) => tag.tagId),
      ]),
    ];

    onChange(updatedSelected);
     //  ADD this line — only when forceOpen is true
    if (forceOpen) {
      handleForceOpenChange(updatedSelected);
    }
    rowAction?.updateTagsCallBack?.(row, updatedSelected);
    setCreatedTag([]);
    const validTags = Array.isArray(tagsList) ? tagsList : [];

    const selectedTags = validTags?.filter((tag) =>
      updatedSelected.includes(tag.tagId)
    );

    const unselectedTags = validTags?.filter(
      (tag) => !updatedSelected.includes(tag.tagId)
    );

    setSortedTags([...selectedTags, ...unselectedTags]);

    handleClose();
  };

  const handleForceOpenChange = (updatedSelected: string[]) => {
    const selectedTagNames = tagsList
      .filter((tag) => updatedSelected.includes(tag.tagId))
      .map((tag) => tag.tagName);
  
    onForceChange?.({
      tagIds: updatedSelected,
      tagNames: selectedTagNames,
    });
  };
  
  const hasChanges = selected.sort().join(",") !== value.map(v => v.tagId).sort().join(",") || createdTag.length > 0;
  const editInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (editAnchor ) {
      setTimeout(() => {
        editInputRef.current?.focus();
      }, 50);
    }
  }, [editAnchor]);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
    }
  }, [forceOpen]);

  const handleTagUpdate = () => {
    const tagToUpdate = filteredTags.find(
      (t) => t.tagId === editAnchor?.tag
    );
    if (!tagToUpdate) return;

    const updatedName = editValue.trim();
    setSortedTags((prev) =>
      prev.map((tag) =>
        tag.tagId === tagToUpdate.tagId
          ? { ...tag, tagName: updatedName }
          : tag
      )
    );
    updateTag({
      tagId: tagToUpdate.tagId,
      name: updatedName,
      isDeleted: false,
    });
  };
  const isEditChanged = editAnchor && editValue.trim() !== editAnchor.originalName?.trim();
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [open]);

  const dividerIndex =
    defaultSelected.length > 0
      ? filteredTags.findIndex(tag => !defaultSelected.includes(tag.tagId))
      : -1;
  return (
    <Box className={noBorder ? "" : `assignee-cell-wrapper ${open ? "assignee-cell-wrapper--active" : ""} ${!canEdit ? "no-hover" : ""}`} 
    onClick={() => {
      if (!forceOpen) handleToggle();
    }}>
      
      <Box
        ref={anchorRef}
        className= {inlineIconMode ? "" : "tags-container"}
        sx={{ width: "100%" }}
      >
        {!forceOpen && (
      <>
        {inlineIconMode ? (
          value.length > 0 ? (
            // ✅ Show first tag + count
            <Tooltip title={tooltipTitle || "Tags"} arrow placement="top">
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Chip
                key={value[0].tagId}
                label={value[0].tagName}
                size="small"
                className="tags-chip-default-icon"
              />
              {value.length > 1 && (
                <Typography sx={{ fontSize: 12, color: "#5F5F70" }}>
                  +{value.length - 1}
                </Typography>
              )}
            </Box>
            </Tooltip>
          ) : (
            // ✅ No selected tags → show default icon
            <Tooltip title={tooltipTitle || "Tags"} arrow placement="top">
              <span>  
              <img
                src={defaultIcon || TagIcon}
                alt="tag"
                style={{ height: 14, width: 14, cursor: "pointer", opacity: 0.8 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggle();
                }}
                />
              </span>
            </Tooltip>
          )
        ) : inlineSearch ? (
          value?.length ? (
            <Box
              className="dropdown-box assignee-box"
              role="button"
              aria-haspopup="true"
              sx={{ border: "1px solid #e0e0e0"}}
            >
              {value?.slice(0, 1).map((tag) => (
                <Chip
                  key={tag.tagId}
                  label={tag.tagName}
                  size="small"
                  className="tags-chip"
                  disabled = {!canEdit && !isNewTask}
                />
              ))}
              {value?.length > 1 && (
                <Typography sx={{ fontSize: 12, color: "#5F5F70" }}>
                  +{value.length - 1}
                </Typography>
              )}
              <img src={dropdownArrowIcon} alt="" style={{ width: "10px", height: "5px", marginLeft: "10px" }} />
            </Box>
          ) : (
            <Box
              className="dropdown-box assignee-box"
              role="button"
              aria-haspopup="true"
              sx={{
                 cursor : canEdit || isNewTask ? "pointer" : "default",
                 border: "1px solid #e0e0e0",
              }}
            >
              <span className="priority-label"
              style={{
                opacity: canEdit || isNewTask? 1 : 0.5,
              }}
              >Select</span>
              <img src={dropdownArrowIcon} alt="arrow" style={{ width: "10px", height: "5px" }} />
            </Box>
          )
        ) : value?.length ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            <Chip
              key={value[0].tagId}
              label={value[0].tagName}
              size="small"
              className="tags-chip"
              sx={{
                maxWidth: value.length === 1 ? "104px" : "80px",
                "& .MuiChip-label": {
                  maxWidth: value.length === 1 ? "104px" : "80px"
                }
              }}
            />
            {value?.length > 1 && (
              <Typography sx={{ fontSize: 12, color: "#5F5F70", ml: 0.5 }}>
                +{value.length - 1}
              </Typography>
            )}
          </Box>
        ) : (
          !hideDefaultIcon && (
            <Box sx={{ pl: 1, color: "text.secondary" }}>
              <img src={TagIcon} alt="" />
            </Box>
          )
        )}
        </>
          )}
      </Box>

      {open && (
        <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" 
        disablePortal={false}
        modifiers={[
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 6],
            },
          },
        ]}
        style={{ zIndex: 1300 }}>
          <ClickAwayListener disableReactTree onClickAway={handleClose}>
            <Paper className="cf-paper-toggle" sx={{ paddingTop: "10px" }} onClick={(e) => e.stopPropagation()}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SearchIcon fontSize="small" sx={{ color: "text.secondary", mr: 1, ml: 1 }} />
                <TextField 
                fullWidth 
                placeholder="Search or add tag..." 
                size="small" 
                variant="standard" 
                value={search} 
                inputRef={searchInputRef}
                onChange={(e) => setSearch(e.target.value)} 
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const newTagName = search.trim();
                    if (!newTagName) return;
                    createTag({ name: newTagName, isDeleted: false });
                  }
                }}
                InputProps={{ disableUnderline: true }} />
              </Box>
              <Divider />
              {/* Tag list */}
              <Box sx={{ height: "230px", overflowY: "auto", p: 0.5, scrollbarWidth: "thin" }}>
                  <>
                    {search.trim() &&
                      !filteredTags.some(
                        (tag) =>
                          tag.tagName.toLowerCase() === search.trim().toLowerCase()
                      ) && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            py: 0.5,
                            px: 1,
                            borderRadius: 1,
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#F5F5F7" },
                          }}
                          onClick={() => {
                            const newTagName = search.trim();
                            if (!newTagName) return;
                            createTag({ name: newTagName, isDeleted: false });
                          }}
                        >
                          <Typography sx={{ fontSize: "11px", color: "#9595A3", paddingRight: "10px" }}>
                            Create
                          </Typography>
                          <Chip label={search.trim()} size="small" className="tags-chip-hover" />
                          <Button>
                            <img src={EnterIcon} alt="" />
                          </Button>
                        </Box>
                      )}
                    {filteredTags.map((tag, index) => {
                      const isHovered = hoveredTag === tag.tagId;
                      const isSelected = selected.length > 0;
                      const showDivider =
                        isSelected && dividerIndex !== null && index === dividerIndex;
                      return (
                        <React.Fragment key={tag.tagId}>
                          {showDivider && <Divider />}
                          <Box
                            onMouseEnter={() => handleMouseEnter(tag.tagId)}
                            onMouseLeave={() => handleMouseLeave(tag.tagId)}
                            className="tag-row"
                            onClick={() => handleSelect(tag)}
                          >
                            <Box className="tags-box">
                              <Typography sx={{ fontSize: "10px" }}>
                                {tag.tagName}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.3,
                              }}
                            >
                              {isHovered && (
                                <MoreHorizIcon
                                  fontSize="small"
                                  sx={{
                                    color: "#6F6F7F",
                                    cursor: "pointer",
                                    opacity: hoveredTag === tag.tagId ? 1 : 0,
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (editAnchor?.tag === tag.tagId) {
                                      setEditAnchor(null);
                                    } else {
                                      setEditAnchor({
                                        anchorEl:
                                          e.currentTarget,
                                        tag: tag.tagId,
                                        originalName: tag.tagName
                                      });
                                      setEditValue(tag.tagName ?? "");
                                    }
                                  }}
                                />
                              )}
                              <Checkbox
                                checked={selected.includes(tag.tagId)}
                                className="cf-checkbox"
                                sx={{ p: 0 }}
                              />
                            </Box>
                          </Box>
                        </React.Fragment>
                      );
                    })}
                  </>
                {editAnchor && (
                  <Popper
                    open
                    anchorEl={editAnchor.anchorEl}
                    placement="right-start"
                    disablePortal={false}
                    modifiers={[
                      { name: "offset", options: { offset: [4, 40] } },
                      { name: "preventOverflow", options: { boundary: "viewport" } },
                    ]}
                    sx={{ zIndex: 2100 }}
                  >
                    <ClickAwayListener onClickAway={() => setEditAnchor(null)}>
                      <Paper
                        sx={{
                          p: 1,
                          boxShadow: 3,
                          borderRadius: 2,
                          width: 300,
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                        onClick={(e) => e.stopPropagation()}>
                        <TextField 
                        size="small" 
                        fullWidth 
                        inputRef={editInputRef}
                        value={editValue} 
                        onChange={(e) => setEditValue(e.target.value)}/>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              const tagToDelete = filteredTags.find((t) => t.tagId === editAnchor?.tag);
                              if (!tagToDelete) return;
                              deleteTag({
                                tagId: tagToDelete.tagId,
                                name: tagToDelete.tagName,
                                isDeleted: true,
                              });
                            }}
                            sx={{
                              textTransform: "none",
                              fontSize: "11px",
                              color: "#E84646",
                              background: "#F2F2F4",
                            }}>
                            Delete
                          </Button>
                          <Box sx={{ display: "flex", gap: 1, marginLeft: "auto" }}>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{
                              backgroundColor: "#6036d6",
                              color:"#ffff",
                              fontWeight: "500",
                              fontSize: "11px",
                              textTransform: "none",
                              "&:hover": { backgroundColor: "#4a27b8" },
                            }}
                            onClick={handleTagUpdate}
                            disabled={!isEditChanged}>
                            Save
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            sx={{
                              backgroundColor: "#F2F2F4",
                              color:"#7A7A8C",
                              fontWeight: "500",
                              fontSize: "11px",
                              textTransform: "none",
                            }}
                            onClick={() => setEditAnchor(null)}>
                            Cancel
                          </Button>
                        </Box>
                        </Box>
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                )}
              </Box>
              <Divider />
              <Box className="cf-multi-apply-container">
                <Button 
                    fullWidth 
                    className={`cf-multi-apply-btn ${!hasChanges ? "disabled" : ""}`} 
                    disabled={!hasChanges} 
                    onClick={handleSave}
                >
                  {saveButtonLabel}
                </Button>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </Box>
  );
};

export default ActionItemsTagsCell;
